import { createContext, useContext, type ReactNode } from 'react';
import { api } from '@/lib/api';
import { refreshSessionData } from '@/lib/sessionData';

// Site-wide PIN gate stays off: status is hardcoded to 'authenticated' so
// browsing is never blocked. login() still mints a session cookie — that is
// required for /api/admin writes and /api/documents. logout() clears it.

interface AuthState {
  status: 'loading' | 'authenticated' | 'anonymous';
  login: (pin: string) => Promise<void>;
  logout: () => Promise<void>;
}

const AuthContext = createContext<AuthState | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const value: AuthState = {
    status: 'authenticated',
    login: async (pin: string) => {
      // Site-wide PIN gate stays off (AUTH_ENABLED=false). This only mints
      // a session cookie so /api/admin writes and /api/documents succeed.
      await api.post('/auth/login', { pin });
      refreshSessionData();
    },
    logout: async () => {
      // Best-effort clear of any lingering session cookie from the previous
      // PIN-gated deploy. Ignore failures — the endpoint may not respond.
      try {
        await api.post('/auth/logout');
      } catch {
        /* ignore */
      }
      refreshSessionData();
    },
  };
  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthState {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
}
